import { Link } from '@inertiajs/react'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import type { SharedMarketingProps } from '@/types/marketing'

interface SuggestionLink {
  label: string
  href: string
  description: string
}

interface NotFoundProps extends SharedMarketingProps {
  attempted_path: string
  suggestions: SuggestionLink[]
}

export default function NotFound({ attempted_path, suggestions }: NotFoundProps) {
  return (
    <section className="marketing-hero-section">
      <div className="container mx-auto px-6 py-20 lg:py-28">
        <ScrollReveal direction="up">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-full" style={{ background: 'rgba(255, 106, 26, 0.08)', border: '1px solid rgba(255, 106, 26, 0.25)' }}>
              <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="#ff6a1a" strokeWidth={1.8} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            <p className="text-xs uppercase tracking-widest text-[#ff6a1a]" style={{ letterSpacing: '0.18em' }}>
              404
            </p>
            <h1 className="marketing-display mt-3 text-3xl lg:text-5xl">
              That page slipped off the record.
            </h1>
            <p className="marketing-lead mt-5 max-w-xl mx-auto">
              We couldn&apos;t find the page at{' '}
              <code className="rounded px-1.5 py-0.5 font-mono text-sm" style={{ background: 'rgba(255, 255, 255, 0.06)', color: 'rgba(226, 232, 240, 0.85)' }}>
                {attempted_path}
              </code>
              . It may have moved, been renamed, or never existed. Try one of the routes
              below — or head back to the home page.
            </p>

            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <Link href="/" className="marketing-button-primary">Back to home</Link>
              <Link href="/contact-us" className="marketing-button-secondary">Contact support</Link>
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal direction="up" delay={0.1}>
          <div className="mx-auto mt-16 max-w-3xl">
            <h2 className="text-center text-xs uppercase tracking-widest" style={{ color: 'rgba(148, 163, 184, 0.6)', letterSpacing: '0.18em' }}>
              Try one of these
            </h2>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {suggestions.map((s) => (
                <li key={s.href}>
                  <Link
                    href={s.href}
                    className="block h-full rounded-xl px-5 py-4 transition-colors"
                    style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.08)'
                    }}
                  >
                    <div className="text-sm font-semibold text-white">{s.label}</div>
                    <div className="mt-1 text-xs leading-relaxed" style={{ color: 'rgba(226, 232, 240, 0.6)' }}>
                      {s.description}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </ScrollReveal>
      </div>
    </section>
  )
}
